import { useState, useEffect } from "react";
import { X, Save, Package2 } from "lucide-react";
import ImageUploader from "../ImageUploader";
import { Product } from "../../types/Product";
import BaseApi from "../../services/base-api";
import { useTranslation } from "react-i18next";
import { LocalizedText } from "../../types/LocalizedText";
import { COLOR_OPTIONS } from "./variation";

interface Category {
  id?: string;
  name: LocalizedText;
  slug: string;
}

interface ProductForm {
  name: LocalizedText;
  sku: string;
  style: string;
  description: LocalizedText;
  price?: number;
  weight: number;
  colors: string[];
  sizes: string[];
  compareAtPrice: number | string;
  categoryId: string;
  images: string[];
  stock: number;
  isActive: boolean;
}

interface ProductFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (product: any) => Promise<void>;
  product: Product | null;
  categoriesProps: Category[];
}

const CreateEditProduct = ({
  isOpen,
  onClose,
  onSave,
  product,
  categoriesProps,
}: ProductFormModalProps) => {
  const isEditing = !!product;
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<Category[]>(categoriesProps);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const { t } = useTranslation();

  const [form, setForm] = useState<ProductForm>({
    name: { en: "", ar: "" },
    sku: "",
    style: "",
    description: { en: "", ar: "" },
    price: undefined,
    weight: 0,
    colors: [],
    sizes: [],
    compareAtPrice: 0,
    categoryId: "",
    images: [],
    stock: 0,
    isActive: true,
  });
  const [errors, setErrors] = useState<any>({});

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const res = await BaseApi.get(
        `/categories?page=${currentPage}&limit=${30}`,
      );
      if (res && res.data) {
        setCategories(res.data);
      }
    } catch (error) {
      console.error(t("Failed to fetch categories"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (product) {
      setForm({
        name: product.name ?? { en: "", ar: "" },
        sku: product.sku ?? "",
        style: product.style ?? "",
        description: product.description ?? { en: "", ar: "" },
        price: product.price ?? 0,
        weight: product.weight ?? 0,
        compareAtPrice: product.compareAtPrice ?? 0,
        categoryId:
          typeof product.category === "string"
            ? product.category
            : (product.category?.id ?? ""),
        colors: product.colors || [],
        sizes: product.sizes || [],
        images: (product.images ?? []) as string[],
        stock: product.stock ?? 0,
        isActive: product.isActive ?? true,
      });
    } else {
      setForm({
        name: { en: "", ar: "" },
        sku: "",
        style: "",
        description: { en: "", ar: "" },
        price: undefined,
        weight: 0,
        compareAtPrice: 0,
        categoryId: "",
        colors: [],
        sizes: [],
        images: [] as string[],
        stock: 0,
        isActive: true,
      });
    }
    fetchCategories();
    setErrors({});
  }, [product, isOpen]);

  const handleChange = (field: keyof ProductForm | string, val: any) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".") as [keyof ProductForm, string];

      setForm((prev) => ({
        ...prev,
        [parent]: {
          ...(prev[parent] as Record<string, any>),
          [child]: val,
        },
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [field as keyof ProductForm]: val,
      }));
    }

    if (field === "name.en" && !isEditing) {
      const sku = val
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

      setForm((prev) => ({ ...prev, sku }));
    }

    if (errors[field]) {
      setErrors((prev: any) => ({ ...prev, [field]: "" }));
    }
  };

  const validate = () => {
    const e: any = {};
    if (!form.name?.en?.trim()) e.nameEn = "English name is required";
    if (!form.name?.ar?.trim()) e.nameAr = "Arabic name is required";
    if (!form.sku.trim()) e.sku = "SKU is required";
    if (!form.price || form.price <= 0)
      e.price = "Price must be greater than 0";
    if (!form.weight || form.weight <= 0)
      e.weight = "Weight in grams is required";
    if (!form.categoryId) e.categoryId = "Category is required";
    if (!form.images || form.images.length === 0)
      e.images = "At least one image is required";
    if (!form.sizes.length) e.sizes = "Sizes are required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      await onSave(form);
      handleClose();
    } catch (error) {
      console.error("Error saving product:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setForm({
      name: { en: "", ar: "" },
      sku: "",
      style: "",
      description: { en: "", ar: "" },
      price: undefined,
      weight: 0,
      compareAtPrice: 0,
      categoryId: "",
      images: [],
      sizes: [],
      colors: [],
      stock: 0,
      isActive: true,
    });
    setErrors({});
    onClose();
  };

  const getSelectedColors = () => {
    return COLOR_OPTIONS.filter((c) => form.colors.includes(c.name));
  };

  const selectedColor = form.colors?.[0] || "";

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-end sm:items-center justify-center"
      onClick={(e) => {
        e.stopPropagation();
      }}
      style={{ animation: "fadeIn 0.3s" }}
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(100%); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      <div
        className="bg-white w-full h-full sm:h-auto sm:rounded-2xl shadow-2xl sm:max-w-3xl sm:max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "slideUp 0.3s" }}
      >
        <div className="bg-gradient-to-r from-black to-gray-700 px-4 sm:px-6 py-4 sm:rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
          <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
            <Package2 className="w-5 h-5 sm:w-6 sm:h-6" />
            {isEditing ? t("Edit Product") : t("Add Product")}
          </h3>
          <button
            onClick={handleClose}
            className="text-white hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("English Name")} *
              </label>
              <input
                type="text"
                value={form.name.en}
                onChange={(e) => handleChange("name.en", e.target.value)}
                className={`w-full px-4 py-3 rounded-lg border-2 ${
                  errors.nameEn
                    ? "border-red-500"
                    : "border-gray-200 focus:border-black"
                } focus:outline-none transition-all`}
                placeholder={t("Product name")}
              />
              {errors.nameEn && (
                <p className="mt-2 text-sm text-red-500">{errors.nameEn}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("Arabic Name")} *
              </label>
              <input
                type="text"
                value={form.name.ar}
                onChange={(e) => handleChange("name.ar", e.target.value)}
                className={`w-full px-4 py-3 rounded-lg border-2 ${
                  errors.nameAr
                    ? "border-red-500"
                    : "border-gray-200 focus:border-black"
                } focus:outline-none transition-all`}
                placeholder="اسم المنتج"
                dir="rtl"
              />
              {errors.nameAr && (
                <p className="mt-2 text-sm text-red-500">{errors.nameAr}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("SKU")} *
              </label>
              <input
                type="text"
                value={form.sku}
                onChange={(e) => handleChange("sku", e.target.value)}
                disabled={isEditing}
                className={`w-full px-4 py-3 rounded-lg border-2 ${
                  errors.sku
                    ? "border-red-500"
                    : "border-gray-200 focus:border-black"
                } ${isEditing ? "bg-gray-100 cursor-not-allowed" : ""} focus:outline-none transition-all`}
                placeholder="product-sku"
              />
              {errors.sku && (
                <p className="mt-2 text-sm text-red-500">{errors.sku}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("Category")} *
              </label>
              <select
                value={form.categoryId}
                onChange={(e) => handleChange("categoryId", e.target.value)}
                className={`w-full px-4 py-3 rounded-lg border-2 ${
                  errors.categoryId
                    ? "border-red-500"
                    : "border-gray-200 focus:border-black"
                } focus:outline-none transition-all`}
              >
                <option value="">Select category</option>
                {(categories || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name?.en ?? ""}
                  </option>
                ))}
              </select>
              {errors.categoryId && (
                <p className="mt-2 text-sm text-red-500">{errors.categoryId}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("Price")} *
              </label>
              <input
                type="number"
                step="0.01"
                value={form.price}
                onChange={(e) =>
                  handleChange("price", parseFloat(e.target.value) || 0)
                }
                className={`w-full px-4 py-3 rounded-lg border-2 ${
                  errors.price
                    ? "border-red-500"
                    : "border-gray-200 focus:border-black"
                } focus:outline-none transition-all`}
                placeholder="0.00"
              />
              {errors.price && (
                <p className="mt-2 text-sm text-red-500">{errors.price}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("Weight (grams)")} *
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={form.weight}
                onChange={(e) =>
                  handleChange("weight", parseInt(e.target.value) || 0)
                }
                className={`w-full px-4 py-3 rounded-lg border-2 ${
                  errors.weight
                    ? "border-red-500"
                    : "border-gray-200 focus:border-black"
                } focus:outline-none transition-all`}
                placeholder="0"
              />
              {errors.weight && (
                <p className="mt-2 text-sm text-red-500">{errors.weight}</p>
              )}
            </div>

            {/* Color Selector */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("Colors")}
              </label>
              <div className="relative">
                <select
                  value={selectedColor}
                  onChange={(e) =>
                    handleChange(
                      "colors",
                      e.target.value ? [e.target.value] : [],
                    )
                  }
                  className="w-full px-4 py-3 pr-10 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all appearance-none"
                >
                  <option value="">Select a color</option>
                  {COLOR_OPTIONS.map((color) => (
                    <option key={color.name} value={color.name}>
                      {color.name}
                    </option>
                  ))}
                </select>
                {form.colors && getSelectedColors() && (
                  <div
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full border-2 border-gray-300"
                    style={{ backgroundColor: getSelectedColors()?.[0]?.hex }}
                  />
                )}
              </div>
              {selectedColor && (
                <div className="mt-2 flex items-center gap-2">
                  <div
                    className="w-8 h-8 rounded-full border-2 border-gray-300"
                    style={{ backgroundColor: getSelectedColors()?.[0]?.hex }}
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {selectedColor}
                  </span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("Style")}
              </label>
              <input
                type="text"
                value={form.style}
                onChange={(e) => handleChange("style", e.target.value)}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all"
                placeholder={t("Product style")}
              />
            </div>

            {/* Size Input */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("Sizes")} *
              </label>
              <input
                type="text"
                value={form.sizes.join(", ")}
                onChange={(e) =>
                  handleChange(
                    "sizes",
                    e.target.value
                      .split(",")
                      .map((size) => size.trim().toUpperCase())
                      .filter(Boolean),
                  )
                }
                placeholder="eg: S, M, L, XL, 32, 34"
                className={`w-full px-4 py-3 rounded-lg border-2 ${
                  errors.sizes
                    ? "border-red-500"
                    : "border-gray-200 focus:border-black"
                } focus:outline-none transition-all`}
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter any custom size (eg: XS, S, M, L, XL, 28, 30, 32)
              </p>
              {errors.sizes && (
                <p className="mt-2 text-sm text-red-500">{errors.sizes}</p>
              )}
            </div>

            {isEditing && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Compare At Price")}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={form.compareAtPrice}
                  onChange={(e) =>
                    handleChange(
                      "compareAtPrice",
                      parseFloat(e.target.value) || 0,
                    )
                  }
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all"
                  placeholder="0.00"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("Stock Quantity")}
              </label>
              <input
                type="number"
                value={form.stock}
                onChange={(e) =>
                  handleChange("stock", parseInt(e.target.value) || 0)
                }
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all"
                placeholder="0"
              />
            </div>
          </div>

          <div className="col-span-full">
            <ImageUploader
              value={form.images}
              onChange={(value: string | string[]) => {
                handleChange("images", value);
              }}
              multiple={true}
              error={errors.images}
              label="Product Images"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t("English Description")}
            </label>
            <textarea
              value={form.description.en || ""}
              onChange={(e) => handleChange("description.en", e.target.value)}
              rows={3}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all"
              placeholder={t("Product description")}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t("Arabic Description")}
            </label>
            <textarea
              value={form.description.ar || ""}
              onChange={(e) => handleChange("description.ar", e.target.value)}
              rows={3}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all"
              placeholder="وصف المنتج"
              dir="rtl"
            />
          </div>

          <div className="pt-4 border-t-2 border-gray-100">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => handleChange("isActive", e.target.checked)}
                className="w-5 h-5 rounded"
              />
              <div>
                <span className="text-sm font-semibold text-gray-700">
                  {t("Active Product")}
                </span>
                <p className="text-xs text-gray-500">
                  {t("Product will be visible on storefront")}
                </p>
              </div>
            </label>
          </div>
        </div>

        <div className="px-4 sm:px-6 pb-4 sm:pb-6 flex flex-col sm:flex-row gap-2 sm:gap-3 sticky bottom-0 bg-white border-t sm:border-0 pt-4">
          <button
            onClick={handleClose}
            className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold transition-all"
          >
            {t("Cancel")}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-700 font-semibold disabled:opacity-70 flex items-center justify-center gap-2 transition-all"
          >
            {saving ? (
              <>
                <div
                  className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                  style={{ animation: "spin 1s linear infinite" }}
                />
                <span>{t("Saving...")}</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                {isEditing ? t("Update Product") : t("Create Product")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateEditProduct;
